import { useContext, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useNavigate } from 'react-router'
import { MoreVert } from '@mui/icons-material'
import { IconButton, Menu, MenuItem, useTheme } from '@mui/material'
import { useSnackbar } from 'notistack'

import MoveDeviceModal from '../../../../components/change-folder-modals/move-device-modal'
import ExternalLinkMenuItem from '../../../../components/external-link-menu-item'
import { AppContext, useHost } from '../../../../providers/app-provider'
import { useAuthentication } from '../../../../providers/auth-provider'
import { reactivateDevice, showDeviceMenuItems } from '../../../../utils/helpers'
import AccountsContext from '../../context'

import useStyles from './index.styles'
import { DeviceTableRow } from './types'

interface IProps {
  device: DeviceTableRow
  setEditDevice: React.Dispatch<React.SetStateAction<DeviceTableRow | undefined>>
  refetchDevices: () => Promise<void>
}

const MoreOptionsMenu = (props: IProps) => {
  const { device, setEditDevice, refetchDevices } = props

  const auth = useAuth()
  const host = useHost()
  const { enqueueSnackbar } = useSnackbar()

  const { setPauseModalStatus, setUnpauseModalStatus, setReturnModalStatus, setDeactivateModalStatus, setActivateModalStatus } = useContext(AccountsContext)
  const { hasInternalUserAccess } = useAuthentication()
  const { tenantId, refreshPage } = useContext(AppContext)

  const navigate = useNavigate()

  const classes = useStyles
  const theme = useTheme()
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const menuOpen = Boolean(menuAnchor)

  const [moveModalOpen, setMoveModalOpen] = useState<boolean>(false)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const handleEditClick = () => {
    handleMenuClose()
    setEditDevice(device)
  }

  const handleDataClick = () => {
    handleMenuClose()
    navigate(`/tenant/${tenantId}/devices/${device.id}?tab=charts`)
  }

  const handlePauseToggleClick = () => {
    if (device.deviceStatus !== 'Paused') setPauseModalStatus({ open: true, devices: [device] })
    else setUnpauseModalStatus({ open: true, devices: [device] })
    handleMenuClose()
  }

  const handleRmaClick = () => {
    handleMenuClose()
    setReturnModalStatus({ open: true, devices: [device] })
  }

  const handleDeactivateClick = () => {
    handleMenuClose()
    setDeactivateModalStatus({ open: true, devices: [device] })
  }

  const handleActivateClick = () => {
    handleMenuClose()
    setActivateModalStatus({ open: true, device })
  }

  const handleReactivateClick = async () => {
    await reactivateDevice(device.id, host, auth.user?.access_token, enqueueSnackbar, refetchDevices)
    handleMenuClose()
  }

  const handleMoveClick = () => {
    handleMenuClose()
    setMoveModalOpen(true)
  }

  const { showActivate, showEditDetails, showPause, showUnpause, showRMA, showDeactivate, resourcesLink, showReactivate } = showDeviceMenuItems(device)

  return (
    <>
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        aria-label="Toggle device quick actions menu"
        sx={{ ...(menuOpen ? classes.moreOptionsBtnSelected(theme) : {}), width: 40 }}
      >
        <MoreVert />
      </IconButton>

      <Menu
        anchorEl={menuAnchor}
        id="quick-actions-menu"
        open={menuOpen}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {showActivate && <MenuItem onClick={handleActivateClick}>Activate</MenuItem>}

        {showEditDetails && hasInternalUserAccess && <MenuItem onClick={handleEditClick}>{showActivate ? 'Edit' : 'Edit Device Solution + Location'}</MenuItem>}

        {resourcesLink && <ExternalLinkMenuItem label="View Resources" href={resourcesLink} onClick={handleMenuClose} />}

        <MenuItem onClick={handleDataClick}>Device Data + Readings</MenuItem>

        {showReactivate && <MenuItem onClick={handleReactivateClick}>Reactivate</MenuItem>}

        {(showPause || showUnpause) && hasInternalUserAccess && (
          <MenuItem onClick={handlePauseToggleClick}>{device.deviceStatus === 'Paused' ? 'Unpause' : 'Pause'}</MenuItem>
        )}

        {showRMA && hasInternalUserAccess && <MenuItem onClick={handleRmaClick}>RMA</MenuItem>}

        {hasInternalUserAccess && <MenuItem onClick={handleMoveClick}>Move</MenuItem>}

        {showDeactivate && hasInternalUserAccess && <MenuItem onClick={handleDeactivateClick}>Deactivate</MenuItem>}
      </Menu>

      {moveModalOpen && <MoveDeviceModal open={true} onClose={() => setMoveModalOpen(false)} devices={[device]} refresh={refreshPage} />}
    </>
  )
}

export default MoreOptionsMenu
